<template>
  <div class="f-main bgImg container learn-page">
    <div class="box" :style="wrapperStyle">
      <!-- 学习计划主卡片：标题 + 橙色 Home + 底部统计条 -->
      <div class="learn-hero">
        <div class="learn-hero-main">
          <div class="learn-hero-left">
            <div class="learn-hero-icon" aria-hidden="true">
              <i class="el-icon-menu" />
            </div>
            <div class="learn-hero-titles">
              <h1 class="learn-hero-title">{{ $t('studentLearn.learningPlan') }}</h1>
              <p class="learn-hero-sub">{{ $t('studentLearn.learningPlanSubtitle') }}</p>
            </div>
          </div>
          <button type="button" class="btn-home-orange" @click="toHome">
            <i class="el-icon-s-home" aria-hidden="true" />
            {{ $t('studentHomepage.home') }}
          </button>
        </div>
        <div class="learn-hero-strip">
          <div class="strip-stat strip-stat--done">
            <i class="el-icon-check" aria-hidden="true" />
            <span>{{ statCompletedN }}</span>
            <span class="strip-label">{{ $t('studentLearn.statCompleted') }}</span>
          </div>
          <div class="strip-stat strip-stat--progress">
            <i class="el-icon-time" aria-hidden="true" />
            <span>{{ statInProgressN }}</span>
            <span class="strip-label">{{ $t('studentLearn.statInProgress') }}</span>
          </div>
          <div class="strip-stat strip-stat--locked">
            <i class="el-icon-lock" aria-hidden="true" />
            <span>{{ statLockedN }}</span>
            <span class="strip-label">{{ $t('studentLearn.statLocked') }}</span>
          </div>
        </div>
      </div>

      <!-- 当前总进度 -->
      <div class="card-surface progress-card">
        <div class="progress-card-head">
          <span class="card-title">{{ $t('studentLearn.currentProgress') }}</span>
          <span class="progress-pct">{{ percentage }} {{ $t('studentLearn.complete') }}</span>
        </div>
        <el-progress
          :percentage="Number(percentage.replace('%', ''))"
          :show-text="false"
          :stroke-width="14"
          color="#7c4dff"
          class="learn-main-progress"
        />
        <p class="progress-card-foot">
          {{ $t('studentLearn.topicsCompletedLine', { done: topicsDoneN, total: topicsTotalN }) }}
        </p>
      </div>

      <!-- 当前焦点列表 -->
      <div class="card-surface focus-card">
        <div class="focus-card-head">
          <div class="focus-card-head-left">
            <span class="focus-diamond" aria-hidden="true">◆</span>
            <span class="card-title">{{ $t('studentLearn.currentTestPreparation') }}</span>
          </div>
          <span class="badge-topics">{{ $t('studentLearn.topicsCountBadge', { n: learningPlan.length }) }}</span>
        </div>
        <div class="focus-list">
          <div
            v-for="item in learningPlan"
            :key="item.id"
            class="focus-row"
            :class="[
              rowClass(item),
              selectLearningPlanId === item.id ? 'selected' : ''
            ]"
            @click="selectLearningPlan(item)"
          >
            <div class="focus-icon-wrap">
              <img
                v-if="topicStatus(item) === 'completed'"
                :src="require('@/assets/image/student/completed.png')"
                alt=""
              >
              <i
                v-else-if="topicStatus(item) === 'locked'"
                class="el-icon-lock focus-icon-ico"
              />
              <i
                v-else
                class="el-icon-time focus-icon-ico focus-icon-ico--active"
              />
            </div>
            <div class="focus-body">
              <span class="focus-name">{{ $i18n.locale === 'en' ? item.pointName : item.pointNameFr }}</span>
              <span class="focus-desc">{{ $i18n.locale === 'en' ? item.description : item.descriptionFr }}</span>
              <el-progress
                v-if="topicStatus(item) === 'progress'"
                :percentage="itemProgressPercent(item)"
                :show-text="false"
                :stroke-width="5"
                color="#7c4dff"
                class="focus-mini-progress"
              />
            </div>
            <div class="focus-status">
              <div class="focus-status-copy">
                <span v-if="topicStatus(item) === 'completed'" class="status-text status-text--done">
                  {{ $t('studentLearn.completedShort') }}
                </span>
                <span v-else-if="topicStatus(item) === 'locked'" class="status-text status-text--locked">
                  {{ $t('studentLearn.locked') }}
                </span>
                <span v-else class="status-text status-text--progress">
                  {{ formatTopicPercent(item) }}%
                </span>
                <span v-if="canRegenerate(item)" class="regeneration-available">
                  {{ $t('studentLearn.newAssessmentAvailable') }}
                </span>
              </div>
              <button
                v-if="canRegenerate(item)"
                type="button"
                class="regeneration-btn"
                :disabled="regenerationNavigatingId === item.id"
                @click.stop="openRegeneration(item)"
              >
                <i
                  :class="regenerationNavigatingId === item.id ? 'el-icon-loading' : 'el-icon-refresh'"
                  aria-hidden="true"
                />
                {{ $t('studentLearn.regenerateLesson') }}
              </button>
              <i class="el-icon-arrow-right focus-chevron" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>

    </div>
    <div class="footer">{{ $t('common.footer') }}</div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { queryWithProgress } from '@/api/tutor/student'

export default {
  name: 'index',
  props: {
    scale: {
      type: Number,
      required: 1
    }
  },
  data () {
    return {
      learningPlan: [],
      selectLearningPlanId: null,
      regenerationNavigatingId: null
    }
  },
  computed: {
    ...mapState('user', {
      userInfo: (state) => state.userInfo
    }),
    wrapperStyle () {
      return {
        transform: `scale(${this.scale})`
      }
    },
    targetStudentId () {
      return this.$route.query.studentId || this.userInfo?.id
    },
    isStudentOwner () {
      return Boolean(
        this.userInfo &&
        this.userInfo.role === 'S' &&
        String(this.userInfo.id) === String(this.targetStudentId)
      )
    },
    percentage () {
      if (this.learningPlan.length === 0) {
        return '0%'
      }
      let answeredCount = 0
      this.learningPlan.forEach(item => {
        if (item.completionPercentage) {
          answeredCount += item.completionPercentage
        }
      })
      const percentage = (answeredCount / (this.learningPlan.length * 100)) * 100
      return (percentage * 100).toFixed(0) + '%'
    },
    /** @returns {number} */
    topicsDoneN () {
      return this.learningPlan.filter(item => this.topicStatus(item) === 'completed').length
    },
    /** @returns {number} */
    topicsTotalN () {
      return this.learningPlan.length
    },
    /** @returns {number} */
    statCompletedN () {
      return this.topicsDoneN
    },
    /** @returns {number} */
    statInProgressN () {
      return this.learningPlan.filter(item => this.topicStatus(item) === 'progress').length
    },
    /** @returns {number} */
    statLockedN () {
      return this.learningPlan.filter(item => this.topicStatus(item) === 'locked').length
    }
  },
  mounted () {
    this.getLearningPlan()
  },
  methods: {
    /**
     * 跳转知识点学习页（与列表点击共用路由参数）
     */
    navigateToPointLearn (item, extraQuery = {}) {
      return this.$router.push({
        path: '/pointLearn',
        query: {
          userId: this.targetStudentId,
          difficultyLevel: item?.difficultyLevel || this.$route.query.difficultyLevel,
          knowledgePointId: item?.id || this.$route.query.knowledgePointId,
          gradeId: this.$route.query.gradeId,
          categoryId: this.$route.query.categoryId,
          pointName: this.$i18n.locale === 'fr' ? item?.pointNameFr : item?.pointName,
          ...extraQuery
        }
      })
    },
    canRegenerate (item) {
      return Boolean(
        this.isStudentOwner &&
        item &&
        item.regeneration &&
        item.regeneration.eligible === true
      )
    },
    openRegeneration (item) {
      if (!this.canRegenerate(item) || this.regenerationNavigatingId) return
      this.regenerationNavigatingId = item.id
      this.navigateToPointLearn(item, {
        regenerate: '1',
        sourceTestRecordId: item.regeneration.sourceTestRecordId || undefined
      }).catch(() => {
        this.regenerationNavigatingId = null
      })
    },
    /**
     * 根据完成度判断行状态（兼容 0–1 与 0–100）
     * @param {{ completionPercentage?: number }} item
     * @returns {'locked'|'completed'|'progress'}
     */
    topicStatus (item) {
      const raw = item.completionPercentage
      if (raw == null || raw === 0) {
        return 'locked'
      }
      if (raw === 100 || raw === 1) {
        return 'completed'
      }
      return 'progress'
    },
    /**
     * @param {{ completionPercentage?: number }} item
     * @returns {string}
     */
    rowClass (item) {
      return 'focus-row--' + this.topicStatus(item)
    },
    /**
     * 进度条数值 0–100
     * @param {{ completionPercentage?: number }} item
     * @returns {number}
     */
    itemProgressPercent (item) {
      const raw = item.completionPercentage
      if (raw == null) {
        return 0
      }
      if (raw <= 1) {
        return Math.min(100, raw * 100)
      }
      return Math.min(100, raw)
    },
    /**
     * 列表右侧百分比展示（与原模板 item.completionPercentage * 100 一致）
     * @param {{ completionPercentage?: number }} item
     * @returns {string}
     */
    formatTopicPercent (item) {
      const raw = item.completionPercentage
      if (raw == null) {
        return '0'
      }
      if (raw <= 1) {
        return (raw * 100).toFixed(0)
      }
      return String(Math.round(raw))
    },
    getLearningPlan () {
      const params = {
        studentId: this.$route.query.studentId,
        gradeId: this.$route.query.gradeId,
        categoryId: this.$route.query.categoryId,
        knowledgePointId: this.$route.query.knowledgePointId
      }
      queryWithProgress(params).then(res => {
        if (res.code !== 200) {
          return false
        }
        this.learningPlan = res.data
      })
    },
    selectLearningPlan (item) {
      this.selectLearningPlanId = item.id === this.selectLearningPlanId ? null : item.id
      this.navigateToPointLearn(item)
    },
    /**
     * 进入下一未完成主题；若全部完成则进入第一项
     */
    onContinueLearning () {
      const list = this.learningPlan
      if (!list.length) {
        return
      }
      const next = list.find(item => this.topicStatus(item) !== 'completed') || list[0]
      this.selectLearningPlanId = next.id
      this.navigateToPointLearn(next)
    },
    toHome () {
      if (this.userInfo.role === 'S') {
        this.$router.push({
          path: '/studentHomepage'
        })
      } else {
        this.$router.push({
          path: '/studentHomepage',
          query: {
            userId: this.$route.query.studentId,
            grade: this.$route.query.gradeId,
            studentName: this.$route.query.studentName
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.learn-page {
  --lp-purple: #7c4dff;
  --lp-purple-deep: #5b21b6;
  --lp-orange: #ff8a00;
  --lp-lavender: #ede7f6;
  --lp-radius: 24px;
  --lp-muted: #64748b;
}

.learn-page.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100% - 90px);
  padding: 12px 16px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f3e8ff 0%, #ede7f6 45%, #e8e0f5 100%);
}

.page-tagline {
  width: 100%;
  max-width: 1480px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: #fff;
  background: var(--lp-purple-deep);
  padding: 8px 16px;
  border-radius: 999px;
  margin-bottom: 12px;
}

.learn-page .box {
  width: 100%;
  max-width: 1443px;
  min-height: 720px;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 24px;
}

/* 顶部学习计划卡片 */
.learn-hero {
  border-radius: var(--lp-radius);
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 12px 40px rgba(91, 33, 182, 0.18);
}

.learn-hero-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px;
  background: linear-gradient(115deg, #8b5cf6 0%, #7c4dff 50%, #9333ea 100%);
}

.learn-hero-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.learn-hero-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  flex-shrink: 0;
}

.learn-hero-title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
}

.learn-hero-sub {
  margin: 6px 0 0;
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.4;
}

.btn-home-orange {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 26px;
  border: none;
  border-radius: 999px;
  background: var(--lp-orange);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(255, 138, 0, 0.45);
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-home-orange:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(255, 138, 0, 0.5);
}

.learn-hero-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  gap: 12px;
  padding: 14px 20px;
  background: rgba(49, 20, 120, 0.42);
  backdrop-filter: blur(6px);
}

.strip-stat {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
}

.strip-stat i {
  font-size: 18px;
  opacity: 0.95;
}

.strip-label {
  font-weight: 600;
  opacity: 0.92;
}

/* 白卡片 */
.card-surface {
  background: #fff;
  border-radius: var(--lp-radius);
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.95);
  padding: 22px 26px;
  margin-bottom: 18px;
}

.progress-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.card-title {
  font-size: 18px;
  font-weight: 800;
  color: #1e1b4b;
}

.progress-pct {
  font-size: 17px;
  font-weight: 800;
  color: var(--lp-purple);
}

.learn-main-progress {
  width: 100%;
}

.learn-main-progress ::v-deep .el-progress-bar__outer {
  border-radius: 999px;
  background-color: #ede7f6;
}

.learn-main-progress ::v-deep .el-progress-bar__inner {
  border-radius: 999px;
}

.progress-card-foot {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--lp-muted);
  font-weight: 500;
}

/* Current Focus */
.focus-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 16px;
}

.focus-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  padding-right: 4px;
}

.focus-card-head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.focus-diamond {
  color: var(--lp-orange);
  font-size: 14px;
  line-height: 1;
}

.badge-topics {
  font-size: 13px;
  font-weight: 700;
  color: var(--lp-purple-deep);
  background: #f3e8ff;
  padding: 6px 14px;
  border-radius: 999px;
}

.focus-list {
  margin-top: 12px;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.focus-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 8px;
  border-bottom: 1px solid #eef0f4;
  cursor: pointer;
  transition: background 0.2s;
}

.focus-row:last-child {
  border-bottom: none;
}

.focus-row:hover {
  background: #faf5ff;
}

.focus-row.selected {
  background: #f3e8ff;
}

.focus-row--locked {
  opacity: 0.72;
}

.focus-row--locked .focus-name,
.focus-row--locked .focus-desc {
  color: #94a3b8;
}

.focus-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
}

.focus-row--completed .focus-icon-wrap {
  background: #dcfce7;
}

.focus-row--progress .focus-icon-wrap {
  background: #ede7ff;
}

.focus-icon-wrap img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.focus-icon-ico {
  font-size: 18px;
  color: #94a3b8;
}

.focus-icon-ico--active {
  color: var(--lp-purple);
}

.focus-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.focus-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.focus-desc {
  font-size: 12px;
  color: var(--lp-muted);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.focus-mini-progress {
  margin-top: 6px;
  max-width: 100%;
}

.focus-mini-progress ::v-deep .el-progress-bar__outer {
  border-radius: 999px;
  background-color: #f1f5f9;
}

.focus-status {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.focus-status-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.regeneration-available {
  max-width: 150px;
  font-size: 11px;
  line-height: 1.2;
  color: #c2410c;
  text-align: right;
}

.regeneration-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid #ddd6fe;
  border-radius: 999px;
  background: #f5f3ff;
  color: var(--lp-purple-deep);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.regeneration-btn:hover:not(:disabled) {
  border-color: #a78bfa;
  background: #ede9fe;
}

.regeneration-btn:disabled {
  cursor: wait;
  opacity: 0.65;
}

.status-text {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.status-text--done {
  color: #16a34a;
}

.status-text--progress {
  color: var(--lp-purple);
}

.status-text--locked {
  color: #94a3b8;
}

.focus-chevron {
  font-size: 14px;
  color: #cbd5e1;
}

/* 底部 CTA */
.learn-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;
  padding: 20px 24px;
  border-radius: var(--lp-radius);
  background: #fff;
  border: 1px solid rgba(124, 77, 255, 0.12);
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
}

.learn-cta-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.learn-cta-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(145deg, #ffedd5, #fdba74);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c2410c;
  font-size: 22px;
  flex-shrink: 0;
}

.learn-cta-title {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: #1e1b4b;
}

.learn-cta-sub {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--lp-muted);
}

.btn-continue {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 26px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(90deg, #7c4dff 0%, #5b21b6 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-continue:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(91, 33, 182, 0.35);
}

.learn-page .footer {
  margin-top: 16px;
  font-size: 11px;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;
}

@media (max-width: 768px) {
  .learn-hero-main {
    flex-direction: column;
    align-items: flex-start;
  }

  .btn-home-orange {
    align-self: flex-end;
  }

  .regeneration-available {
    display: none;
  }

  .regeneration-btn {
    padding: 0 9px;
    font-size: 0;
  }

  .regeneration-btn i {
    font-size: 14px;
  }
}
</style>
